import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ChatDraft } from '@components/Chat';
import type { ScopedAssistantFirstMessage } from '@components/ScopedAssistantChat';
import { clearPendingUploadFiles, peekPendingUploadFiles } from '@utils/pendingFiles';
import { prefillOf } from '../builderPrefill';

// The route state is read once and cleared (keeping the search), so a reload or going back never resends it.
export const useBuilderChatInbox = () => {
  const navigate = useNavigate();
  const { pathname, search, hash, state } = useLocation();
  const [arrived] = useState(() => prefillOf(state));
  const [initialSend] = useState<ScopedAssistantFirstMessage | undefined>(() =>
    arrived?.send ? { text: arrived.text, files: peekPendingUploadFiles() ?? [] } : undefined,
  );
  const [draft, setDraft] = useState<ChatDraft | undefined>(() =>
    arrived && !arrived.send ? { text: arrived.text, nonce: 1 } : undefined,
  );

  useEffect(() => {
    if (prefillOf(state)) navigate({ pathname, search, hash }, { replace: true, state: null });
  }, [hash, navigate, pathname, search, state]);

  useEffect(() => {
    if (initialSend) clearPendingUploadFiles();
  }, [initialSend]);

  const toChat = useCallback((text: string, send: boolean) => {
    setDraft((current) => ({ text, send, nonce: (current?.nonce ?? 0) + 1 }));
  }, []);
  const draftInChat = useCallback((text: string) => toChat(text, false), [toChat]);
  const sendInChat = useCallback((text: string) => toChat(text, true), [toChat]);

  return { initialSend, draft, draftInChat, sendInChat };
};
